// Logique du pilotage (timeline, taux de marge, dépenses) — PURE et testée.
//
// Ce module porte une responsabilité qui n'est pas cosmétique : rendre VISIBLE
// ce qui rendrait une courbe trompeuse. Une série temporelle bâtie sur des
// sources qui changent en cours de route ressemble à un signal métier alors
// qu'elle décrit un changement de périmètre. C'est déjà arrivé sur ces données :
// deux canaux ont été gelés les 9 et 10 avril 2026, et tout ce qui traverse
// cette date compare deux mondes différents.

/** Bornes proposées au curseur de période. 90 jours par défaut. */
export const FENETRES = [7, 14, 30, 60, 90, 180, 365];

export function fenetreLaPlusProche(jours) {
  const n = Number(jours);
  if (!Number.isFinite(n)) return 90;
  return FENETRES.reduce((a, b) => (Math.abs(b - n) < Math.abs(a - n) ? b : a), FENETRES[0]);
}

/**
 * Détecte les RUPTURES DE PÉRIMÈTRE dans une série.
 *
 * Une source qui disparaît (ou apparaît) entre deux périodes déplace le
 * périmètre mesuré. Comparer « avant » et « après » revient alors à comparer
 * deux choses différentes — et c'est invisible sur une courbe, qui montrera
 * simplement une baisse.
 *
 * `sources` : [{periode, source, n}]
 * Retourne : [{periode, disparues[], apparues[]}]
 */
export function detecterRuptures(sources) {
  if (!Array.isArray(sources) || !sources.length) return [];

  const parPeriode = new Map();
  for (const s of sources) {
    const cle = String(s.periode);
    if (!parPeriode.has(cle)) parPeriode.set(cle, new Set());
    if (s.source) parPeriode.get(cle).add(s.source);
  }

  const periodes = [...parPeriode.keys()].sort();
  const ruptures = [];
  for (let i = 1; i < periodes.length; i++) {
    const avant = parPeriode.get(periodes[i - 1]);
    const apres = parPeriode.get(periodes[i]);
    const disparues = [...avant].filter((s) => !apres.has(s));
    const apparues = [...apres].filter((s) => !avant.has(s));
    if (disparues.length || apparues.length) {
      ruptures.push({ periode: periodes[i], disparues, apparues });
    }
  }
  return ruptures;
}

/**
 * Une série est-elle comparable de bout en bout ?
 * Sert à décider si on autorise une phrase du type « en hausse de X points ».
 */
export function serieComparable(sources) {
  return detecterRuptures(sources).length === 0;
}

/**
 * Prépare les points d'une courbe : échelle, coordonnées, et marquage des
 * points dont la couverture est trop faible pour être affirmés.
 *
 * `seuilCouverture` : en dessous, le point est tracé mais signalé comme peu
 * fiable. On ne le supprime pas — un trou dans une courbe se remarque moins
 * qu'un point marqué, et se lit à tort comme « rien ne s'est passé ».
 */
export function preparerCourbe(lignes, { champ = "taux_marge_pct", seuilCouverture = 60 } = {}) {
  const points = (Array.isArray(lignes) ? lignes : [])
    .map((l) => ({
      periode: String(l.periode),
      valeur: l[champ] == null ? null : Number(l[champ]),
      couverture: l.pct_couverture == null ? null : Number(l.pct_couverture),
      achat: l.achat_ht == null ? null : Number(l.achat_ht),
      ecartees: Number(l.n_ecartees || 0),
    }))
    .filter((p) => p.valeur != null && Number.isFinite(p.valeur));

  if (!points.length) return { points: [], min: 0, max: 0, fiables: 0 };

  for (const p of points) {
    p.fiable = p.couverture != null && p.couverture >= seuilCouverture;
  }

  const valeurs = points.map((p) => p.valeur);
  let min = Math.min(...valeurs);
  let max = Math.max(...valeurs);
  // Toujours inclure zéro sur un taux : une courbe qui flotte entre 11 et 13
  // sans le zéro visible donne l'illusion d'un effondrement.
  min = Math.min(min, 0);
  if (max === min) max = min + 1;

  return {
    points,
    min,
    max,
    fiables: points.filter((p) => p.fiable).length,
  };
}

/**
 * Évolution entre le premier et le dernier point — mais SEULEMENT si la série
 * est comparable. Sinon on refuse de conclure, explicitement.
 */
export function evolution(lignes, sources) {
  const { points } = preparerCourbe(lignes);
  if (points.length < 2) return { possible: false, raison: "pas assez de périodes" };
  if (!serieComparable(sources)) {
    return {
      possible: false,
      raison: "les sources de données ont changé sur la période — les deux bouts ne sont pas comparables",
    };
  }
  const debut = points[0].valeur;
  const fin = points[points.length - 1].valeur;
  return { possible: true, debut, fin, delta: Math.round((fin - debut) * 10) / 10 };
}

/**
 * Marge RÉALISÉE par laboratoire (`rentabilite_labo`) — prépare ce que l'écran affiche.
 *
 * Rien n'est recalculé ici : la vue `v_rentabilite_labo` a déjà tranché le coût
 * (PU moyen pondéré sur la fenêtre) et la couverture. Cette fonction ne fait que
 * classer, borner et nommer :
 *  · classement par marge en euros, les lignes SANS marge en dernier — jamais en
 *    tête, où un `null` trié en premier occuperait tout le haut du tableau ;
 *  · la part de chaque labo dans la marge totale de la période, pour lire
 *    « 32 % de ma marge vient d'un seul grossiste » sans calculer de tête ;
 *  · au-delà de `max` lignes, le reste est AGRÉGÉ en une ligne nommée, pas
 *    coupé : un tableau tronqué sans le dire se lit comme un tableau complet.
 */
export function preparerRentabilite(lignes, { max = 20 } = {}) {
  const rows = (Array.isArray(lignes) ? lignes : []).filter((l) => l && l.laboratoire);
  const avecMarge = rows.filter((l) => l.marge_eur != null).map((l) => ({ ...l, marge_eur: Number(l.marge_eur) }));
  const sansMarge = rows.filter((l) => l.marge_eur == null);
  avecMarge.sort((a, b) => b.marge_eur - a.marge_eur || String(a.laboratoire).localeCompare(String(b.laboratoire)));
  const totalMarge = avecMarge.reduce((s, l) => s + l.marge_eur, 0);
  const totalCa = avecMarge.reduce((s, l) => s + Number(l.ca_ht_couvert ?? 0), 0);
  const part = (l) => (totalMarge > 0 ? Math.round((l.marge_eur / totalMarge) * 1000) / 10 : null);

  const tetes = avecMarge.slice(0, max).map((l) => ({
    laboratoire: l.laboratoire,
    ca_ht_couvert: Number(l.ca_ht_couvert ?? 0),
    marge_eur: l.marge_eur,
    taux_marge_pct: l.taux_marge_pct == null ? null : Number(l.taux_marge_pct),
    nb_produits: Number(l.nb_produits ?? 0),
    produits_extrapoles: Number(l.produits_extrapoles ?? 0),
    part_pct: part(l),
  }));
  const reste = avecMarge.slice(max);
  const agrege = reste.length
    ? {
        laboratoire: `${reste.length} autres laboratoires`,
        ca_ht_couvert: reste.reduce((s, l) => s + Number(l.ca_ht_couvert ?? 0), 0),
        marge_eur: reste.reduce((s, l) => s + l.marge_eur, 0),
        taux_marge_pct: null,
        nb_produits: reste.reduce((s, l) => s + Number(l.nb_produits ?? 0), 0),
        produits_extrapoles: reste.reduce((s, l) => s + Number(l.produits_extrapoles ?? 0), 0),
        part_pct: totalMarge > 0 ? Math.round((reste.reduce((s, l) => s + l.marge_eur, 0) / totalMarge) * 1000) / 10 : null,
        agrege: true,
      }
    : null;
  return {
    lignes: tetes,
    agrege,
    nbLabos: avecMarge.length,
    nbSansMarge: sansMarge.length,
    totalMarge: Math.round(totalMarge * 100) / 100,
    totalCa: Math.round(totalCa * 100) / 100,
    tauxGlobal: totalCa > 0 ? Math.round((totalMarge / totalCa) * 1000) / 10 : null,
  };
}
